import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft, Facebook, Twitter, Linkedin, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import BlogSidebar from "@/components/BlogSidebar";

export default function BlogPostSoftwavePlantarFasciitis() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Can SoftWave TRT Cure Plantar Fasciitis? | Longview, TX"
        description="Struggling with sharp heel pain in the morning? Learn how SoftWave TRT offers a non-invasive, highly effective treatment for Plantar Fasciitis in East Texas."
        ogImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/c8e3792a-6f6f-4202-9477-c8e4235a9e41.png&w=1280&output=webp&q=80"
        canonical="https://woodsspine.com/blog/softwave-plantar-fasciitis"
      />
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-12">
        <Button variant="ghost" className="mb-6 hover:bg-transparent pl-0 text-muted-foreground hover:text-foreground" asChild>
          <Link to="/blog">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
        </Button>
        <div className="lg:grid lg:grid-cols-[1fr_350px] gap-12">
          <div className="space-y-8">
            <header className="space-y-4">
              <Badge className="bg-accent text-white hover:bg-accent/90 border-0 shadow-sm">
                SoftWave TRT
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight text-primary">
                Can SoftWave TRT Cure Plantar Fasciitis? A Non-Surgical Solution
              </h1>
              <div className="flex flex-wrap items-center text-sm text-muted-foreground gap-4">
                <Link to="/about#dr-tucker-woods" className="flex items-center hover:text-accent transition-colors">
                  <User className="w-4 h-4 mr-2" />
                  Dr. Tucker Woods
                </Link>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  April 10, 2026
                </span>
                <span>• 5 min read</span>
              </div>
            </header>

            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/c8e3792a-6f6f-4202-9477-c8e4235a9e41.png&w=1280&output=webp&q=80" 
              alt="SoftWave TRT treatment for Plantar Fasciitis foot pain in Longview TX" 
              className="w-full rounded-2xl aspect-video object-cover"
              fetchPriority="high"
              decoding="async"
            />

            <article className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-xl font-medium text-foreground">
                If the first steps out of bed in the morning feel like walking on glass, you are likely dealing with plantar fasciitis. This incredibly common condition affects millions, but traditional treatments often fall short. Discover how SoftWave Tissue Regeneration Technology (TRT) provides a non-invasive path to lasting relief.
              </p>
              <p>
                Plantar fasciitis is notorious for being stubborn. Many patients spend months—or even years—trying different orthotics, painful steroid injections, and night splints with little to no success. In this article, we will explore why the plantar fascia is so difficult to heal, and how advanced acoustic wave therapy is changing the way we treat chronic foot pain.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">What is Plantar Fasciitis?</h2>
              <p>
                The plantar fascia is a thick band of connective tissue that runs across the bottom of your foot, connecting your heel bone to your toes. It acts like a shock-absorbing bowstring, supporting the arch of your foot. 
              </p>
              <p>
                When the tension and stress on this bowstring become too great, small tears can occur in the fascia. Repeated stretching and tearing lead to inflammation, irritation, and the sharp, stabbing pain characteristic of plantar fasciitis. Because feet have relatively poor blood supply compared to muscles, these micro-tears struggle to heal efficiently on their own.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">How SoftWave TRT Heals the Fascia</h2>
              <p>
                <Link to="/longview-softwave-pain-management-tissue-regeneration" className="text-accent hover:underline">SoftWave TRT</Link> uses patented electrohydraulic shockwave technology to generate unfocused acoustic waves. When these waves are directed at the bottom of the foot, they penetrate deep into the damaged plantar fascia.
              </p>
              <p>
                This process does three critical things:
              </p>
              <ul>
                <li><strong>Increases Blood Flow:</strong> The acoustic waves stimulate angiogenesis—the creation of new blood vessels. This floods the poorly vascularized plantar fascia with the oxygen and nutrients needed for repair.</li>
                <li><strong>Reduces Inflammation:</strong> SoftWave immediately down-regulates the inflammatory markers causing your morning heel pain.</li>
                <li><strong>Activates Stem Cells:</strong> The therapy tricks your body into thinking a new injury has occurred, recruiting your own resident stem cells to actively rebuild the torn connective tissue.</li>
              </ul>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Clinical Evidence and Credibility</h2>
              <p>
                Extracorporeal shockwave therapy (ESWT), the technology behind SoftWave, is widely recognized in the medical community as a highly effective treatment for plantar fasciitis. It is FDA-cleared and supported by numerous peer-reviewed clinical studies showing significant reductions in pain and improvements in foot function. Unlike cortisone injections, which can actually weaken the plantar fascia and increase the risk of rupture, SoftWave strengthens and regenerates the tissue.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Local Relief in Longview, TX</h2>
              <p>
                At Woods Spine & Wellness in Longview, TX, Dr. Tucker Woods and Dr. Lance Potter have seen firsthand how debilitating plantar fasciitis can be for our East Texas patients. Whether you are a nurse on your feet all day at Good Shepherd, or just trying to enjoy a walk around the neighborhood, we are dedicated to providing advanced, drug-free solutions right here in Gregg County. You don't have to live with foot pain. Learn more about our approach on our <Link to="/what-we-treat#plantar-fasciitis" className="text-accent hover:underline">What We Treat</Link> page.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-6 mt-6">
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">How many SoftWave sessions do I need for plantar fasciitis?</h3>
                  <p className="text-muted-foreground">While many patients notice a reduction in pain after just 1 or 2 sessions, a typical protocol for chronic plantar fasciitis involves 6 to 8 sessions to ensure complete tissue regeneration and long-lasting results.</p>
                </div>
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Is the treatment painful on a sensitive heel?</h3>
                  <p className="text-muted-foreground">The treatment is well tolerated. Because SoftWave uses unfocused waves, it is much gentler than older, focused shockwave devices. You may feel a pulsing sensation over the most inflamed areas, but it is generally not painful.</p>
                </div>
                <div className="pb-2">
                  <h3 className="text-lg font-bold text-primary mb-2">Will I need to wear a boot or stop walking?</h3>
                  <p className="text-muted-foreground">No. One of the biggest advantages of SoftWave TRT is that there is zero downtime. You can walk out of the clinic and return to your normal daily activities immediately after your session.</p>
                </div>
              </div>

            </article>

            {/* Social Sharing */}
            <div className="flex items-center gap-4 pt-8 border-t border-border">
              <span className="font-medium text-muted-foreground">Share this article:</span>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <LinkIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* CTA Block */}
            <div className="bg-secondary/30 p-8 rounded-xl border border-border mt-8">
              <h3 className="text-2xl font-bold text-primary mb-3">Take your first pain-free step</h3>
              <p className="text-muted-foreground mb-6">
                If morning heel pain is keeping you from living your life, schedule a consultation to see if SoftWave TRT is right for your plantar fasciitis.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">Get Started</Link>
                </Button>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="text-sm text-muted-foreground border-t border-border pt-6 mt-8">
              Disclaimer: The information provided in this article is for educational purposes only and does not constitute medical advice. Please consult with a healthcare provider before starting any new treatment.
            </div>
          </div>

          <BlogSidebar />
        </div>
      </main>
      <Footer />
    </div>
  );
}
